import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Dropdown } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { updateCategory } from '../../../../../../../../crud/menu_api/categories';
import { getAllMenuCategoryTypes } from '../../../../../../../../crud/menu_api/menu_category_types';
import { RawParentsCategorys, ParentsCategorys } from '../../../../types';
import './UpdateCategory.scss';
import { getWorkstationsOptions, WorkstationOption, getMenusOptions, MenuOption } from '../../../utils/utils';
/* ---------- Tipos y método para obtener las estaciones de trabajo ---------- */


/* -------------------------- Componente UpdateCategory -------------------------- */
/*
  Se actualizan los tipos de los props para que currentData, setCurrentData,
  setCategories y demás sean del mismo tipo (RawParentsCategorys[]). Además,
  al construir el objeto de edición se conserva la propiedad 'menus' proveniente del parent.
*/
interface UpdateCategoryProps {
  showEditModal: boolean;
  setShowEditModal: (show: boolean) => void;
  currentData: RawParentsCategorys[];
  setCurrentData: (data: RawParentsCategorys[]) => void;
  currentCategory: ParentsCategorys;
  originalCategories: ParentsCategorys[]; // O el mismo tipo que uses en Table
  setOriginalCategories: (data: ParentsCategorys[]) => void;
  setCategories: (data: ParentsCategorys[]) => void;
}

const UpdateCategory: React.FC<UpdateCategoryProps> = ({
  showEditModal,
  setShowEditModal,
  currentData,
  setCurrentData,
  currentCategory,
  originalCategories,
  setOriginalCategories,
  setCategories,
}) => {
  // Inicializamos los estados utilizando las propiedades del parentCategory
  const [workstations, setWorkstations] = useState<WorkstationOption[]>([]);
  const [isActive, setIsActive] = useState<boolean>(currentCategory.state);
  const [isMain, setIsMain] = useState<boolean>(currentCategory.is_main);
  const [categoryName, setCategoryName] = useState<string>(currentCategory.category_name || '');
  const [workstationId, setWorkstationId] = useState<number>(currentCategory.workstation_id || 0);
  const [loading, setLoading] = useState(false);
  // Estado para el dropdown de tipos de categoría (ahora como objeto)
  const [menuCategoryType, setMenuCategoryType] = useState<{ id: number, name: string }>({
    id: currentCategory.menu_category_type_id ?? 0,
    name: currentCategory.menu_category_type_name || '',
  });
  const [menuCategoryTypes, setMenuCategoryTypes] = useState<any[]>([]);

  // Menus state
  const [menus, setMenus] = useState<MenuOption[]>([]);
  const [selectedMenuId, setSelectedMenuId] = useState<number>(0);

  // Cargar opciones de estaciones de trabajo, tipos de categoría y menus
  useEffect(() => {
    getWorkstationsOptions().then((response) => {
      if (response) {
        setWorkstations(response);
      }
    });

    getAllMenuCategoryTypes()
      .then((response: any) => {
        if (response && response.data) {
          setMenuCategoryTypes(response.data);
        } else {
          setMenuCategoryTypes([]);
        }
      })
      .catch(() => {
        setMenuCategoryTypes([]);
      });

    getMenusOptions().then(resp => {
      if (resp && resp.length > 0) {
        setMenus(resp);
        // Select first menu by default, never allow 'Ninguno' (0)
        setSelectedMenuId(resp[0].id);
      }
    });
  }, []);

  // Actualiza los estados cuando cambia currentCategory
  useEffect(() => {
    setIsActive(currentCategory.state);
    setIsMain(currentCategory.is_main);
    setCategoryName(currentCategory.category_name);
    setWorkstationId(currentCategory.workstation_id || 0);
    setMenuCategoryType({
      id: currentCategory.menu_category_type_id ?? 0,
      name: currentCategory.menu_category_type_name || '',
    });
    // If category has menus, select the first one, else keep default
    if (currentCategory.menus && currentCategory.menus.length > 0) {
      setSelectedMenuId(currentCategory.menus[0].menu_id);
    }
  }, [currentCategory]);

  // Reiniciar los campos cuando se cierra el modal
  useEffect(() => {
    if (!showEditModal) {
      setIsActive(false);
      setIsMain(false);
      setCategoryName('');
      setWorkstationId(0);
      setMenuCategoryType({ id: 0, name: '' });
      setSelectedMenuId(0);
    }
  }, [showEditModal]);

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    e.preventDefault();
    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;
    // Construimos el objeto de edición con el nombre del tipo seleccionado
    const edit_category: ParentsCategorys = {
      ...currentCategory,
      category_name: categoryName,
      is_main: isMain,
      workstation_id: workstationId, // Usar el valor del estado
      local_id: localId,
      state: isActive,
      menu_category_type_id: menuCategoryType.id && menuCategoryType.id > 0 ? menuCategoryType.id : null,
      menu_category_type_name: menuCategoryType.name || currentCategory.menu_category_type_name,
    };

    updateCategory(Number(edit_category.category_id), edit_category)
      .then((response) => {
        toast.success('Categoría editada con éxito!');
        const updatedCategory: ParentsCategorys = (response as any).data;
        updatedCategory.menu_category_type_name = menuCategoryType.name || updatedCategory.menu_category_type_name;
        // Actualizar solo la categoría editada en los arrays
        const newOriginalCategory = originalCategories.map(cat =>
          cat.category_id === updatedCategory.category_id ? updatedCategory : cat
        );
        setCategories(newOriginalCategory);
        setOriginalCategories(newOriginalCategory);
        setCurrentData(newOriginalCategory);
        setShowEditModal(false);
      })
      .catch((error) => {
        console.error('Error al editar la categoría:', error);
        if(error.response.status===401){
          toast.error('Debe seleccionar un tipo de categoría')
        }else{
        toast.error('Error al editar la categoría');
        }
      }).finally(() => setLoading(false));
  };  

  return (
    <Modal 
      show={showEditModal} 
      onHide={() => setShowEditModal(false)}
      centered 
      className="update-category-modal"
    >
      <Form onSubmit={handleFormSubmit}>
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">
              Actualizar categoría
            </Modal.Title>
          </div>
          <Button 
            variant="none"
            className="modal-close-button"
            onClick={() => setShowEditModal(false)}
          >
            <X size={24} />
          </Button>
        </Modal.Header>

        <Modal.Body>
          <div className="form-container">
            <Form.Group controlId="category_name" className="mp-formgroup horizontal">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                type="text"
                name="category_name"
                placeholder="Ingresa el nombre de la categoría"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                required
              />
            </Form.Group>

            {/* Workstation Dropdown */}
            <Form.Group className="mp-formgroup horizontal">
              <Form.Label>Estación de trabajo</Form.Label>
              <Dropdown
                onSelect={(eventKey: string | null) =>
                  setWorkstationId(eventKey ? Number(eventKey) : 0)
                }
              >
                <Dropdown.Toggle
                  variant="light"
                  id="dropdown-workstation"
                  className="dropdown-toggle"
                >
                  {workstationId
                    ? workstations.find(w => w.id === workstationId)?.name
                    : 'Seleccionar estación'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="0">
                    -- Ninguna --
                  </Dropdown.Item>
                  {workstations.map(ws => (
                    <Dropdown.Item key={ws.id} eventKey={ws.id.toString()}>
                      {ws.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>

            {/* Visibility Options */}
            <Form.Group className="mp-formgroup horizontal">
              <div>
                <Form.Label>Visibilidad del menú</Form.Label>
                <Form.Text className="text-muted">
                  Elige si la categoría será visible para los clientes
                </Form.Text>
              </div>
              <div className="visibility-options">
                <div 
                  className={`visibility-option visible ${isMain ? 'selected' : ''}`}
                  onClick={() => setIsMain(true)}
                >
                  <FaEye className="option-icon" />
                  <span className="option-text">Visible</span>
                </div>
                <div 
                  className={`visibility-option hidden ${!isMain ? 'selected' : ''}`}
                  onClick={() => setIsMain(false)}
                >
                  <FaEyeSlash className="option-icon" />
                  <span className="option-text">Oculta</span>
                </div>
              </div>
            </Form.Group>

            {/* Category Type Dropdown */}
            <Form.Group className="mp-formgroup horizontal">
              <Form.Label>Tipo de categoría</Form.Label>
              <Dropdown
                onSelect={(eventKey: string | null) => {
                  if (!eventKey) return;
                  const selectedId = Number(eventKey);
                  const selectedType = menuCategoryTypes.find(type => type.menu_category_type_id === selectedId);
                  if (selectedType) {
                    setMenuCategoryType({ id: selectedId, name: selectedType.name });
                  }
                }}
              >
                <Dropdown.Toggle
                  variant="light"
                  id="dropdown-category-type"
                  className="dropdown-toggle"
                >
                  {menuCategoryType.id
                    ? menuCategoryTypes.find((t: any) => t.menu_category_type_id === menuCategoryType.id)?.name
                    : 'Seleccionar tipo'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item eventKey="0">-- Seleccionar tipo --</Dropdown.Item>
                  {menuCategoryTypes.map((type: any) => (
                    <Dropdown.Item
                      key={type.menu_category_type_id}
                      eventKey={type.menu_category_type_id.toString()}
                    >
                      {type.name}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <Button 
              className="form-button-cancel" 
              variant="secondary" 
              onClick={() => setShowEditModal(false)}
            >
              Cancelar
            </Button>
            <Button 
              className="form-button-create" 
              variant="primary" 
              type="submit"
              disabled={loading || !menuCategoryType.id || !workstationId}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>{' '}
                  Actualizando...
                </>
              ) : (
                'Actualizar'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UpdateCategory;
